import * as React from "react";
import apiClient from "api";
import { useApolloClient } from "@apollo/client";
import { setPauseAuthRefresh } from "../apollo";
import { GetMeasurementStation_station } from "../utils/__generated__/GetMeasurementStation";
import { setLocale } from "../i18n";
import { nativeAppBridge } from "utils/nativeAppBridge";
import { MfaLoginChallenge } from "utils/mfa";

const MOBILE_SESSION_TOKEN_KEY = "mgSessionToken";

export interface OrganizationRoles {
  organizationId: number;
  role: string;
}

export interface User {
  id: number;
  displayName: string;
  email: string;
  admin?: boolean;
  organizationRoles: OrganizationRoles[];
  locale: string;
  canEditFormulas: boolean;
  canEditStationParameters: boolean;
  // What the session may only do for now: set up MFA, or wait for the login
  // to be confirmed from an email (utils/mfa RESTRICTION_*)
  restriction?: string | null;
}
export interface AuthState {
  loggedIn: boolean;
  user?: User;
}
export const initialAuthState = {
  loggedIn: false,
  user: undefined,
};

interface AuthContextProps {
  auth: AuthState;
  setAuth: (auth: AuthState) => void;
}
const AuthContext = React.createContext<AuthContextProps>({
  auth: initialAuthState,
  setAuth: () => {},
});

export const AuthProvider: React.FC = ({ children }) => {
  const [auth, setAuth] = React.useState<AuthState>(initialAuthState);
  const contextValue = React.useMemo(
    () => ({ auth, setAuth }),
    [auth, setAuth],
  );
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Utils

export const isOrgAdmin = (
  user: User | undefined,
  organization_id: number | null,
): boolean => {
  if (!organization_id) {
    return false;
  }
  return (
    user?.admin ||
    !!user?.organizationRoles.find(
      (org) =>
        org.role === "admin" && organization_id === Number(org.organizationId),
    )
  );
};

export const isAnyOrgAdmin = (user: User | undefined): boolean => {
  return !!user?.organizationRoles.find((org) => org.role === "admin");
};

export const isStationAdmin = (
  user: User | undefined,
  station?: GetMeasurementStation_station,
): boolean => {
  return isOrgAdmin(user, station?.organization_id || 0);
};

export const isStationQualityController = (
  user: User | undefined,
  station: GetMeasurementStation_station,
): boolean => {
  return (
    user?.admin ||
    !!user?.organizationRoles.find(
      (org) =>
        ["quality_controller", "admin"].includes(org.role) &&
        station.organization_id === Number(org.organizationId),
    )
  );
};

// Hooks
export const useAuth = (): AuthState => React.useContext(AuthContext).auth;

export const useIsAdmin = (
  station?: GetMeasurementStation_station,
): boolean => {
  const { user } = useAuth();
  if (user?.admin) {
    return true;
  }
  return isStationAdmin(user, station);
};
export const useIsQualityController = (
  station: GetMeasurementStation_station,
): boolean => {
  const { user } = useAuth();
  return isStationQualityController(user, station);
};

export const useCurrentUser = (refresh: boolean = false) => {
  const { setAuth, auth } = React.useContext(AuthContext);
  return async (): Promise<LoginStatus> => {
    if (auth.loggedIn && !refresh) {
      return {
        success: true,
        user: auth.user,
      };
    }

    let response;
    try {
      response = await apiClient.request("/auth/current-user");
    } catch (e) {
      console.error("Failed to load current user", e);
    }

    if (!response?.user) {
      setAuth({
        loggedIn: false,
      });
      return {
        success: false,
      };
    }

    const locale = response.user.locale;
    const authState: AuthState = {
      loggedIn: true,
      user: response.user,
    };
    setAuth(authState);
    setLocale(locale);
    return {
      success: true,
      user: authState.user,
    };
  };
};

export interface LoginProps {
  email: string;
  password: string;
  remember?: boolean;
}
export interface LoginStatus {
  success: boolean;
  user?: User;
  error?: string;
  // The password was right; the login needs a second factor (useFinishLogin)
  mfa?: MfaLoginChallenge;
}

/**
 * Completes a login once the server has created the session: passes the
 * session to the mobile app and loads the user
 */
export const useFinishLogin = () => {
  const retrieveCurrentUser = useCurrentUser(true);
  const apolloClient = useApolloClient();
  return async (response: Record<string, any>): Promise<LoginStatus> => {
    let error;
    // A JWT of an earlier session must not be used for the new one
    apiClient.discardAuthJWT();

    if (nativeAppBridge.isNativeConnected && response?.session_token) {
      window.localStorage.setItem(
        MOBILE_SESSION_TOKEN_KEY,
        response.session_token,
      );
      nativeAppBridge.proactivelySendSessionToken();
    }

    let user;
    try {
      user = await retrieveCurrentUser();
    } catch (e) {
      error = String(e);
      console.error("Failed to load current user", e);
    }
    if (!user?.success) {
      return {
        success: false,
        error,
      };
    }

    // Clear apollo client so any non-permissions are cleared
    await apolloClient.resetStore();

    return user;
  };
};

export const useLogin = () => {
  const finishLogin = useFinishLogin();
  return async ({
    email,
    password,
    remember = false,
  }: LoginProps): Promise<LoginStatus> => {
    let response;
    let error;
    try {
      response = await apiClient.request("/auth/login-user", {
        method: "POST",
        data: {
          email,
          password,
          persistent: remember,
        },
      });
    } catch (e) {
      error = String(e);
      console.error("#1 Login failed", e);
    }

    if (response?.code === 2 && response.mfa) {
      return { success: false, mfa: response.mfa as MfaLoginChallenge };
    }
    if (response?.code !== 1) {
      return {
        success: false,
        error,
      };
    }
    return finishLogin(response);
  };
};

export const onLogoutUser = () => {
  apiClient.request(
    "/auth/token/logout",
    {
      method: "POST",
    },
    false,
  );
  window.localStorage.removeItem("authJWT");
  window.localStorage.removeItem(MOBILE_SESSION_TOKEN_KEY);

  if (nativeAppBridge.isNativeConnected) {
    nativeAppBridge.notifyLogout();
  }
};

export const useLogout = () => {
  const apolloClient = useApolloClient();
  const { setAuth } = React.useContext(AuthContext);
  return () => {
    setPauseAuthRefresh(true); // Prevent apollo ws from refreshing the auth token while logging out
    setAuth({ loggedIn: false, user: undefined });
    onLogoutUser();
    apolloClient
      .resetStore()
      .then(() => {
        setPauseAuthRefresh(false);
      })
      .catch((e) => {
        console.error("reseting apollo store failed, refreshing", e);
        window.location.reload();
      });
  };
};

export interface PasswordRecoveryRequestProps {
  email: string;
}
export const usePasswordRecoveryRequest =
  () =>
  async ({ email }: PasswordRecoveryRequestProps): Promise<LoginStatus> => {
    const response = await apiClient.request("/auth/request-recovery-token", {
      method: "POST",
      data: { email },
    });
    const { success } = response;
    return { success };
  };

export interface PasswordRecoverySubmitProps {
  email: string;
  recoveryToken: string;
  password: string;
  confirmPassword: string;
}
export const usePasswordRecoverySubmit =
  () =>
  async ({
    email,
    recoveryToken,
    password,
    confirmPassword,
  }: PasswordRecoverySubmitProps): Promise<LoginStatus> => {
    const response = await apiClient.request("/auth/submit-recovery-token", {
      method: "POST",
      data: { email, recoveryToken, password, confirmPassword },
    });
    const { success } = response;
    return { success };
  };
